import React, { useState } from 'react';
import axios from 'axios';
import { Briefcase, MapPin, Clock, CheckCircle, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CareersSection = () => {
  const [selectedJob, setSelectedJob] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position_applied: '',
    qualification: '',
    year_of_passing: '',
    cover_letter: '',
    cv_filename: 'Resume.pdf (simulated)'
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const jobs = [
    {
      title: 'Article Assistant (CA Foundation / Intermediate)',
      location: 'Jaipur Office',
      type: 'Full Time',
      requirements: [
        'CA Foundation or Intermediate passed',
        'Willingness to learn and work in audit and taxation',
        'Good communication skills'
      ]
    },
    {
      title: 'Semi-Qualified CA / CA Final Student',
      location: 'Jaipur Office',
      type: 'Full Time',
      requirements: [
        'CA Final student with both groups cleared/attempted',
        'Strong knowledge of accounting standards and taxation',
        'Ability to handle client assignments independently'
      ]
    },
    {
      title: 'Audit Executive (Experience: 1–3 years)',
      location: 'Jaipur Office',
      type: 'Full Time',
      requirements: [
        '1-3 years of post-qualification experience',
        'Expertise in statutory audits, tax audits, and internal audits',
        'Strong analytical and report writing skills'
      ]
    },
    {
      title: 'Tax Associate – GST & Income Tax',
      location: 'Jaipur Office',
      type: 'Full Time',
      requirements: [
        'Graduate in Commerce or CA Inter/Final',
        'Experience in GST compliance and income tax return filing',
        'Familiarity with tax software and portals'
      ]
    },
    {
      title: 'Accounts Executive',
      location: 'Jaipur Office',
      type: 'Full Time',
      requirements: [
        'M.Com / CA Inter or equivalent',
        'Experience in bookkeeping, accounts finalization, and MIS',
        'Proficiency in Tally and MS Excel'
      ]
    }
  ];

  const handleApplyClick = (job) => {
    setSelectedJob(job);
    setFormData(prev => ({ ...prev, position_applied: job.title }));
    setSubmitSuccess(false);
    setErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.qualification.trim()) newErrors.qualification = 'Qualification is required';
    if (!formData.year_of_passing.trim()) newErrors.year_of_passing = 'Year of passing is required';
    if (!formData.cover_letter.trim() || formData.cover_letter.length < 30) {
      newErrors.cover_letter = 'Cover letter must be at least 30 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/career/apply`, formData);
      setSubmitSuccess(true);
      
      // Close modal and reset form after 2 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
        setSelectedJob(null);
        setFormData({
          name: '',
          email: '',
          phone: '',
          position_applied: '',
          qualification: '',
          year_of_passing: '',
          cover_letter: '',
          cv_filename: 'Resume.pdf (simulated)'
        });
      }, 2000);
    } catch (error) {
      console.error('Error submitting application:', error);
      alert('Failed to submit application. Please try again or email your resume directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0D1B2A] via-[#1a2d42] to-[#0D1B2A]">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(0deg, #C9A84C, #C9A84C 1px, transparent 1px, transparent 20px),
                           repeating-linear-gradient(90deg, #C9A84C, #C9A84C 1px, transparent 1px, transparent 20px)`
        }}></div>
      </div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-playfair font-bold text-[#C9A84C] mb-4">
            Careers
          </h2>
          <div className="w-24 h-1 bg-[#C9A84C] mx-auto mb-6"></div>
          <p className="text-gray-300 font-dmsans max-w-3xl mx-auto">
            Join our team of professionals and grow your career in a dynamic environment with 
            opportunities for learning, international exposure, and professional development.
          </p>
        </div>

        {/* Why Join Section */}
        <div className="bg-[#1a2d42] bg-opacity-60 backdrop-blur-sm p-8 rounded-lg shadow-lg mb-12 border border-[#C9A84C] border-opacity-20">
          <h3 className="text-2xl font-playfair font-bold text-[#C9A84C] mb-4 text-center">
            Why Join AGDG & Associates?
          </h3>
          <p className="text-gray-300 font-dmsans leading-relaxed text-justify">
            At AGDG & Associates, we believe in nurturing talent and providing a platform for continuous 
            learning and professional growth. With offices in Jaipur and Dubai, we offer exposure to 
            diverse industries, international standards, and cutting-edge practices in accounting, 
            auditing, and taxation. Join us to work alongside experienced professionals, handle challenging 
            assignments, and build a rewarding career in chartered accountancy.
          </p>
        </div>

        {/* Job Listings */}
        <div className="mb-12">
          <h3 className="text-3xl font-playfair font-bold text-[#C9A84C] mb-8 text-center">
            Current Openings
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {jobs.map((job, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-t-4 border-[#C9A84C]"
              >
                <div className="flex items-start gap-3 mb-4">
                  <Briefcase className="text-[#C9A84C] flex-shrink-0 mt-1" size={24} />
                  <h4 className="text-lg font-playfair font-bold text-[#0D1B2A]">
                    {job.title}
                  </h4>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600 font-dmsans">
                    <MapPin size={16} className="text-[#C9A84C]" />
                    {job.location}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600 font-dmsans">
                    <Clock size={16} className="text-[#C9A84C]" />
                    {job.type}
                  </div>
                </div>

                <div className="mb-6">
                  <p className="text-sm font-dmsans font-semibold text-[#0D1B2A] mb-2">
                    Key Requirements:
                  </p>
                  <ul className="space-y-1">
                    {job.requirements.map((req, reqIndex) => (
                      <li key={reqIndex} className="text-xs text-gray-700 font-dmsans flex items-start">
                        <span className="text-[#C9A84C] mr-2 mt-1">•</span>
                        <span>{req}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={() => handleApplyClick(job)}
                  className="w-full bg-[#C9A84C] text-[#0D1B2A] py-2 rounded font-dmsans font-semibold hover:bg-[#b8973d] transition-colors"
                >
                  Apply Now
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Application Form Modal */}
        {selectedJob && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full my-8">
              <div className="bg-[#0D1B2A] text-white p-6 rounded-t-lg flex justify-between items-center">
                <div>
                  <h3 className="text-2xl font-playfair font-bold mb-1">Apply for Position</h3>
                  <p className="text-sm text-gray-300 font-dmsans">{selectedJob.title}</p>
                </div>
                <button
                  onClick={() => setSelectedJob(null)}
                  className="text-white hover:text-[#C9A84C] transition-colors"
                >
                  <X size={28} />
                </button>
              </div>

              {submitSuccess ? (
                <div className="p-12 text-center">
                  <CheckCircle className="text-green-500 mx-auto mb-4" size={64} />
                  <h4 className="text-2xl font-playfair font-bold text-green-800 mb-2">
                    Application Submitted!
                  </h4>
                  <p className="text-gray-700 font-dmsans">
                    Thank you for applying. We will review your application and get back to you soon.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-dmsans font-semibold text-[#0D1B2A] mb-1">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-[#C9A84C] ${
                          errors.name ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-dmsans font-semibold text-[#0D1B2A] mb-1">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-[#C9A84C] ${
                          errors.email ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-dmsans font-semibold text-[#0D1B2A] mb-1">
                        Phone <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-[#C9A84C] ${
                          errors.phone ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-dmsans font-semibold text-[#0D1B2A] mb-1">
                        Qualification <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="qualification"
                        value={formData.qualification}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-[#C9A84C] ${
                          errors.qualification ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="e.g., CA Inter, B.Com"
                      />
                      {errors.qualification && <p className="text-red-500 text-xs mt-1">{errors.qualification}</p>}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-dmsans font-semibold text-[#0D1B2A] mb-1">
                      Year of Passing <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="year_of_passing"
                      value={formData.year_of_passing}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-[#C9A84C] ${
                        errors.year_of_passing ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="e.g., 2023"
                    />
                    {errors.year_of_passing && <p className="text-red-500 text-xs mt-1">{errors.year_of_passing}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-dmsans font-semibold text-[#0D1B2A] mb-1">
                      Upload CV
                    </label>
                    <div className="px-3 py-2 border border-gray-300 rounded bg-gray-50 text-sm font-dmsans text-gray-600">
                      Resume upload (simulated) - {formData.cv_filename}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-dmsans font-semibold text-[#0D1B2A] mb-1">
                      Cover Letter <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="cover_letter"
                      value={formData.cover_letter}
                      onChange={handleChange}
                      rows="4"
                      className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-[#C9A84C] ${
                        errors.cover_letter ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Tell us why you're a good fit for this position..."
                    ></textarea>
                    {errors.cover_letter && <p className="text-red-500 text-xs mt-1">{errors.cover_letter}</p>}
                  </div>

                  <div className="flex gap-4 pt-4">
                    <button
                      type="button"
                      onClick={() => setSelectedJob(null)}
                      className="flex-1 py-3 border-2 border-gray-300 text-gray-700 rounded font-dmsans font-semibold hover:bg-gray-100 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`flex-1 py-3 rounded font-dmsans font-semibold transition-colors ${
                        isSubmitting
                          ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                          : 'bg-[#C9A84C] text-[#0D1B2A] hover:bg-[#b8973d]'
                      }`}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Application'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default CareersSection;
